
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ReactRouterEnhancerProps {
  children: React.ReactNode;
}

/**
 * Component that enhances React Router for better SEO and crawlability
 */
export const ReactRouterEnhancer = ({ children }: ReactRouterEnhancerProps) => {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on route change for better user experience
    window.scrollTo(0, 0);
    
    // Update document title if not already set by SEO component
    if (!document.title.includes('ReCut')) {
      document.title = 'ReCut App';
    }
    
    // Add structured data for navigation
    const addNavigationStructuredData = () => {
      const existingScript = document.getElementById('navigation-structured-data');
      if (existingScript) {
        existingScript.remove();
      }
      
      const navigationData = {
        "@context": "https://schema.org",
        "@type": "SiteNavigationElement",
        "name": "ReCut Navigation",
        "url": window.location.origin,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${window.location.origin}/search?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      };
      
      const script = document.createElement('script');
      script.id = 'navigation-structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(navigationData);
      document.head.appendChild(script);
    };
    
    // Add breadcrumb structured data based on current path
    const addBreadcrumbStructuredData = () => {
      const existingScript = document.getElementById('breadcrumb-structured-data');
      if (existingScript) {
        existingScript.remove();
      }
      
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const breadcrumbs = [
        { name: 'Accueil', url: '/' }
      ];
      
      let currentPath = '';
      pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        let name = segment;
        
        // Translate common path segments
        const translations: Record<string, string> = {
          'channels': 'Chaînes',
          'channel': 'Chaîne',
          'clips': 'Clips',
          'clip': 'Clip',
          'playlists': 'Playlists',
          'playlist': 'Playlist',
          'derivatives': 'Dérivés',
          'derivative': 'Dérivé',
          'creators': 'Créateurs',
          'creator-profile': 'Profil Créateur',
          'LamourCestSurcote': 'L\'amour c\'est surcoté'
        };
        
        name = translations[segment] || segment;
        
        breadcrumbs.push({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          url: currentPath
        });
      });
      
      if (breadcrumbs.length > 1) {
        const breadcrumbData = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((crumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.name,
            "item": `${window.location.origin}${crumb.url}`
          }))
        };
        
        const script = document.createElement('script');
        script.id = 'breadcrumb-structured-data';
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(breadcrumbData);
        document.head.appendChild(script);
      }
    };
    
    // Add structured data after a short delay to ensure DOM is ready
    setTimeout(() => {
      addNavigationStructuredData();
      addBreadcrumbStructuredData();
    }, 100);
    
    // Track route changes for analytics (if available)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
        page_path: location.pathname + location.search,
      });
    }
    
  }, [location]);

  return <>{children}</>;
};
